import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";

import { AssetPhotosTab } from "@/features/assets/components/asset-detail-tabs/AssetPhotosTab";
import { useLookupAssetSpecs } from "@/features/assets/hooks/useLookupAssetSpecs";

import { AIAssessmentCard } from "./AIAssessmentCard";
import { DocumentsCard } from "./DocumentsCard";
import { SpecificationsCard } from "./SpecificationsCard";
import { useAssetDocuments } from "./useAssetDocuments";

const MAX_RELATED_SPEC_SHEETS = 2;

/**
 * The asset detail body shared by the drawer (`AssetDetailPanel`) and the
 * full-screen page (`ClientAssetDetail`), so both always show the same
 * sections: specifications, AI assessment, photo gallery and documents.
 */
export function AssetDetailContent({
  asset,
  enabled = true,
}: {
  asset: Asset;
  enabled?: boolean;
}) {
  const {
    status: specLookupStatus,
    skipReason: specSkipReason,
    primaryPdf,
    relatedPdfs,
    message: specLookupMessage,
    loading: loadingSpecSheets,
  } = useLookupAssetSpecs({
    assetId: asset.id,
    processingStatus: asset.processing_status,
    enabled,
  });

  const visibleRelatedPdfs = relatedPdfs
    .filter((p) => p.file_url)
    .slice(0, MAX_RELATED_SPEC_SHEETS);
  const specSheetCount =
    (primaryPdf?.file_url ? 1 : 0) + visibleRelatedPdfs.length;

  const { documents, photos, loadingDocuments, fetchDocumentsAndPhotos } = useAssetDocuments(
    asset.id,
    enabled
  );

  return (
    <div className="space-y-6">
      <SpecificationsCard asset={asset} />

      <AIAssessmentCard asset={asset} />

      {/* Photo Gallery — same logic as full view (signed URLs, lightbox, download).
          AssetPhotosTab self-pads with `p-6` for the tabbed layouts; cancel it here so the
          card aligns flush with the sibling Specifications/Documents cards. */}
      <div className="[&>div]:!p-0 [&_h3]:!text-base">
        <AssetPhotosTab
          photos={photos.map((p) => ({
            ...p,
            category: p.category ?? "",
            uploaded_by: p.uploaded_by ?? "",
            mime_type: p.mime_type ?? undefined,
          }))}
          assetId={asset.id}
          onRefetch={fetchDocumentsAndPhotos}
        />
      </div>

      <DocumentsCard
        documents={documents}
        loadingDocuments={loadingDocuments}
        specSheetCount={specSheetCount}
        loadingSpecSheets={loadingSpecSheets}
        specLookupStatus={specLookupStatus}
        specSkipReason={specSkipReason}
        specLookupMessage={specLookupMessage}
        primaryPdf={primaryPdf}
        visibleRelatedPdfs={visibleRelatedPdfs}
      />
    </div>
  );
}
